import { useEffect, useRef, useState } from 'react';

import {
    ReservationDaySummaryDTO,
    ReservationDTO,
    Reservations,
} from 'api/controllers/reservations';
import { useAppSelector } from 'index';
import { SignalREvent, signalRService } from 'services/signalR';
import { OVERDUE_CHECK_INTERVAL_MS } from './hooks';

export const DAY_SUMMARY_REFRESH_DELAY_MS = 300;

export type ReservationDaySummaryState =
    | { status: 'loading' }
    | { status: 'error' }
    | { status: 'loaded'; summary: ReservationDaySummaryDTO };

/** Capacity summary of the whole day; reloads when reservations of the restaurant change in real time. */
export function useReservationDaySummary(day: string | null): ReservationDaySummaryState {
    const restaurantId = useAppSelector((state) => state.user.currentRestaurantId);
    const changeRevision = useAppSelector((state) => state.reservations.changeRevision);
    const [state, setState] = useState<ReservationDaySummaryState>({ status: 'loading' });
    const requestId = useRef(0);
    const scheduleRefresh = useRef<(() => void) | null>(null);
    const previousRevision = useRef(changeRevision);

    useEffect(() => {
        if (!day) {
            return;
        }
        let timer: number | undefined;
        let isActive = true;
        let hasOpenRequests = false;
        let isLoading = false;

        const load = (background = false) => {
            const current = ++requestId.current;
            isLoading = true;
            if (!background) setState({ status: 'loading' });
            Promise.resolve()
                .then(() => Reservations.getDaySummary(day))
                .then((summary) => {
                    if (isActive && current === requestId.current) {
                        hasOpenRequests = summary.requests > 0;
                        setState({ status: 'loaded', summary });
                    }
                })
                .catch(() => {
                    if (isActive && current === requestId.current) {
                        setState({ status: 'error' });
                    }
                })
                .finally(() => {
                    if (current === requestId.current) isLoading = false;
                });
        };
        const scheduleLoad = (reservation: ReservationDTO) => {
            if (reservation.restaurantId !== restaurantId) {
                return;
            }
            scheduleRefresh.current?.();
        };
        scheduleRefresh.current = () => {
            ++requestId.current;
            if (timer !== undefined) window.clearTimeout(timer);
            timer = window.setTimeout(() => load(), DAY_SUMMARY_REFRESH_DELAY_MS);
        };

        setState({ status: 'loading' });
        load();
        const expiryTimer = window.setInterval(() => {
            if (hasOpenRequests && !isLoading) load(true);
        }, OVERDUE_CHECK_INTERVAL_MS);
        const refreshOnReturn = () => {
            if (document.visibilityState === 'visible') scheduleRefresh.current?.();
        };
        window.addEventListener('focus', refreshOnReturn);
        document.addEventListener('visibilitychange', refreshOnReturn);
        const unsubscribeReconnected = signalRService.subscribeReconnected(() =>
            scheduleRefresh.current?.()
        );
        const unsubscribeAdded = signalRService.subscribe(
            SignalREvent.ReservationAdded,
            scheduleLoad
        );
        const unsubscribeChanged = signalRService.subscribe(
            SignalREvent.ReservationChanged,
            scheduleLoad
        );

        return () => {
            isActive = false;
            scheduleRefresh.current = null;
            if (timer !== undefined) window.clearTimeout(timer);
            window.clearInterval(expiryTimer);
            window.removeEventListener('focus', refreshOnReturn);
            document.removeEventListener('visibilitychange', refreshOnReturn);
            unsubscribeReconnected();
            unsubscribeAdded();
            unsubscribeChanged();
        };
    }, [day, restaurantId]);

    useEffect(() => {
        if (previousRevision.current !== changeRevision) {
            previousRevision.current = changeRevision;
            scheduleRefresh.current?.();
        }
    }, [changeRevision]);

    return state;
}
